import { TILE_COLS, TILE_MAX_ROWS, defaultSpanFor, minSpanFor, readTileBody } from '../../config/tileSlotPolicy';

/**
 * The pure model behind the tile-layout editor — the displayTemplateModel.js
 * split: every rule lives here and is unit-tested, so the component only has
 * to render and drag.
 *
 * A body is rows of placements, in sixths (KPR-346):
 *   { cols: 6, rows: [ [ { element, span } ], [ … ] ] }
 * Every body this module returns says `cols: 6`. One read from storage that
 * does not (none is stored any more: KPR-761) is no layout on the way in
 * (normalize, sameBody — tileSlotPolicy readTileBody), so the editor only
 * ever works in sixths and saves sixths.
 *
 * Invariants this module maintains, so the editor cannot author a body the
 * API would refuse (utils/cardLayouts/validate.js):
 *   - at most TILE_MAX_ROWS rows, no empty row;
 *   - a row's spans sum to at most TILE_COLS;
 *   - every span is at least its type's minimum, widened on the way in;
 *   - an element appears once in the body.
 *
 * Long text is a field like any other since 2026-10-03 (four columns at
 * least); alone in the body at all six columns the tile reads it as its
 * paragraph (resolveTileLayout).
 */

export const emptyBody = () => ({ cols: TILE_COLS, rows: [] });

/**
 * Does a tile show its item's tags? Yes unless the layout says `tags: false`
 * (ruled d15: tags show by default, and a layout can turn them off). Only the
 * opt-out is ever stored — a body that shows tags says nothing about them —
 * so an arrangement saved before tags existed, or by an API that knows no
 * `tags` key, reads as "show".
 */
// Read through readTileBody: a body that is not in sixths is no layout, so it
// cannot hide the tags of a tile that is drawn as if it had none.
export const tileShowsTags = body => readTileBody(body)?.tags !== false;

/** The body with its tags switch set: `tags: false` when off, no key when on. */
export function withTileTags(body, show) {
	const { tags: _drop, ...rest } = body || emptyBody();
	return show ? rest : { ...rest, tags: false };
}

const clone = body => ({ cols: TILE_COLS, rows: (body?.rows || []).map(row => row.map(p => ({ ...p }))) });

export const placedNames = body => new Set((body?.rows || []).flat().map(p => p.element));

export const rowWidth = row => (row || []).reduce((sum, p) => sum + (p.span || 1), 0);

const liveRows = rows => rows.filter(row => row.length);

/**
 * Where a placement of this element could go: the indexes of the rows with
 * room for it, plus whether a NEW row could hold it. The editor uses this to
 * grey what it cannot drop, so a refusal is visible before the drag rather
 * than after.
 */
export function canPlace(body, element, byName) {
	const dataType = byName.get(element)?.dataType;
	if (!dataType) return { rows: [], newRow: false, reason: 'unknown' };
	if (placedNames(body).has(element)) return { rows: [], newRow: false, reason: 'already-placed' };

	const rows = body?.rows || [];
	const span = minSpanFor(dataType);
	const fits = rows.map((row, index) => (rowWidth(row) + span <= TILE_COLS ? index : null)).filter(i => i !== null);
	const newRow = rows.length < TILE_MAX_ROWS;
	return { rows: fits, newRow, reason: fits.length || newRow ? null : 'full' };
}

/** How wide a field lands where `room` columns are free: what it wants, down to its minimum. */
const fittedSpan = (wanted, min, room) => Math.max(min, Math.min(wanted, room));

/**
 * Adds an element at the end of `rowIndex`, or on a new row when `rowIndex`
 * is null. Widened to its type's minimum, and given what it would like
 * (long text all six columns) where the row has the room. Returns the body
 * unchanged when the placement is not legal, so a caller may try
 * optimistically.
 */
export function addPlacement(body, element, byName, rowIndex = null) {
	const where = canPlace(body, element, byName);
	const dataType = byName.get(element)?.dataType;
	const min = minSpanFor(dataType);
	const wanted = defaultSpanFor(dataType);
	const next = clone(body);

	if (rowIndex === null || rowIndex === undefined) {
		if (!where.newRow) return body;
		next.rows.push([{ element, span: wanted }]);
		return next;
	}
	if (!where.rows.includes(rowIndex)) return body;
	next.rows[rowIndex].push({ element, span: fittedSpan(wanted, min, TILE_COLS - rowWidth(next.rows[rowIndex])) });
	return next;
}

/** Drops one placement, and the row with it when that empties the row. */
export function removePlacement(body, element) {
	const next = clone(body);
	next.rows = liveRows(next.rows.map(row => row.filter(p => p.element !== element)));
	return next;
}

/** Widens or narrows a placement, never below its type's minimum. */
export function setSpan(body, element, span, byName) {
	const dataType = byName.get(element)?.dataType;
	const min = minSpanFor(dataType);
	const wanted = Math.max(min, Math.min(TILE_COLS, span));
	const next = clone(body);
	next.rows = next.rows.map(row => {
		if (!row.some(p => p.element === element)) return row;
		const widened = row.map(p => (p.element === element ? { ...p, span: wanted } : p));
		return rowWidth(widened) > TILE_COLS ? row : widened;
	});
	return next;
}

/** The widest this placement could be in its row as it stands. */
export function maxSpanFor(body, element) {
	const row = (body?.rows || []).find(r => r.some(p => p.element === element));
	if (!row) return TILE_COLS;
	const own = row.find(p => p.element === element).span || 1;
	return TILE_COLS - rowWidth(row) + own;
}

/** Reorders within a row — the only ordering the tile expresses. */
export function reorderInRow(body, rowIndex, from, to) {
	const next = clone(body);
	const row = next.rows[rowIndex];
	if (!row || from === to || from < 0 || to < 0 || from >= row.length || to >= row.length) return body;
	const [moved] = row.splice(from, 1);
	row.splice(to, 0, moved);
	return next;
}

/**
 * Where a placement sits in its row, in COLUMNS: the left edge and the
 * width, so a position on the row (0–6, fractional) can be read against it.
 */
const extents = row => {
	let start = 0;
	return row.map(p => {
		const extent = { start, span: p.span || 1 };
		start += extent.span;
		return extent;
	});
};

/** Which index a field dropped at column position `col` takes in `row`. */
export const insertionIndex = (row, col) => extents(row)
	.filter(({ start, span }) => start + span / 2 <= col).length;

/**
 * THE DROP. Where `element` ends up when it is let go at `target`, and what
 * moves out of its way — or null when nothing it could do would fit.
 *
 * `target` is one of
 *   { type: 'row', row, col }  — onto row `row`, at column position `col`
 *                                (0–6, fractional: the pointer's place on it)
 *   { type: 'newRow', at }     — a row of its own, inserted before row `at`
 *                                (`at` = the row count appends it)
 * Row indexes are into the body as it was BEFORE the drag, which is what the
 * editor measured.
 *
 * A row with room takes the field where it was dropped, at the width it had
 * (or would like), narrowed to the room down to its minimum. A FULL row makes
 * room: first by narrowing what is wider than it needs to be (the field it
 * was dropped on first), so a field dropped on a long text across the tile
 * sits beside it. Failing that, the field takes the place of the one it was
 * dropped on, and that field — with its neighbours to the right, then the
 * left, until there is room — moves to the next row TOWARD WHERE THE DRAGGED
 * FIELD CAME FROM, so
 * dropping a field onto one above it swaps the two, and a field brought in
 * from the list pushes down. When that row has no room either they get a new
 * row on that side; failing that, the other side is tried.
 *
 * Returns { body, displaced: [names] }.
 */
export function planDrop(body, element, byName, target) {
	const dataType = byName.get(element)?.dataType;
	if (!dataType || !target) return null;
	const rows = body?.rows || [];
	const fromRow = rows.findIndex(row => row.some(p => p.element === element));
	const held = fromRow >= 0 ? rows[fromRow].find(p => p.element === element) : null;
	const min = minSpanFor(dataType);
	// What it would like to be: the width the author gave it, or for a field
	// coming in, what its type defaults to.
	const wanted = held ? Math.max(min, held.span || 1) : defaultSpanFor(dataType);

	// Lift it out with the rows left standing, so every index still means
	// what it meant when the drag began.
	const lifted = rows.map(row => row.filter(p => p.element !== element).map(p => ({ ...p })));
	const done = displaced => ({ body: { cols: TILE_COLS, rows: liveRows(lifted) }, displaced });
	const roomForRow = () => liveRows(lifted).length < TILE_MAX_ROWS;

	if (target.type === 'newRow') {
		if (!roomForRow()) return null;
		const at = Math.max(0, Math.min(target.at, lifted.length));
		lifted.splice(at, 0, [{ element, span: wanted }]);
		return done([]);
	}

	const row = lifted[target.row];
	if (!row) return null;
	const col = Math.max(0, Math.min(TILE_COLS, Number(target.col) || 0));
	const room = TILE_COLS - rowWidth(row);
	if (room >= min) {
		row.splice(insertionIndex(row, col), 0, { element, span: fittedSpan(wanted, min, room) });
		return done([]);
	}

	// A full row. The field it was dropped on first, then its neighbours
	// rightward, then leftward…
	const spans = extents(row);
	let hovered = spans.findIndex(({ start, span }) => col >= start && col < start + span);
	if (hovered < 0) hovered = row.length - 1;
	const order = [
		...row.map((_, i) => i).filter(i => i >= hovered),
		...row.map((_, i) => i).filter(i => i < hovered).reverse(),
	];

	// …narrow, where they are wider than they need to be: a field dropped on
	// a long text across the tile sits beside it rather than pushing it off
	// its row.
	const slack = row.reduce((sum, p) => sum + (p.span || 1) - minSpanFor(byName.get(p.element)?.dataType), 0);
	if (room + slack >= min) {
		let needed = min - room;
		for (const index of order) {
			if (needed <= 0) break;
			const p = row[index];
			const give = Math.min(needed, (p.span || 1) - minSpanFor(byName.get(p.element)?.dataType));
			p.span -= give;
			needed -= give;
		}
		row.splice(insertionIndex(row, col), 0, { element, span: min });
		return done([]);
	}

	// …and failing that, move out of its way.
	const leaving = new Set();
	let freed = room;
	for (const index of order) {
		if (freed >= min) break;
		leaving.add(index);
		freed += row[index].span || 1;
	}
	const moving = row.filter((_, i) => leaving.has(i));
	const staying = row.filter((_, i) => !leaving.has(i));
	const at = row.filter((p, i) => i < hovered && !leaving.has(i)).length;
	const movingWidth = rowWidth(moving);
	// Where they were on the row, so they land at the same place on the next.
	const movingCenter = moving.length
		? (spans[row.indexOf(moving[0])].start + movingWidth / 2)
		: 0;

	const place = direction => {
		const step = direction === 'up' ? -1 : 1;
		const neighbour = lifted[target.row + step];
		const next = lifted.map(r => r.map(p => ({ ...p })));
		next[target.row] = [...staying.map(p => ({ ...p }))];
		next[target.row].splice(at, 0, { element, span: fittedSpan(wanted, min, freed) });
		if (neighbour && rowWidth(neighbour) + movingWidth <= TILE_COLS) {
			const into = next[target.row + step];
			into.splice(insertionIndex(into, movingCenter), 0, ...moving.map(p => ({ ...p })));
			return next;
		}
		if (liveRows(next).length < TILE_MAX_ROWS) {
			next.splice(direction === 'up' ? target.row : target.row + 1, 0, moving.map(p => ({ ...p })));
			return next;
		}
		return null;
	};

	const toward = fromRow >= 0 && fromRow < target.row ? 'up' : 'down';
	const placed = place(toward) || place(toward === 'up' ? 'down' : 'up');
	if (!placed) return null;
	lifted.splice(0, lifted.length, ...placed);
	return done(moving.map(p => p.element));
}

/**
 * The keyboard's moves for a placed field — ← → along its row, ↑ ↓ to the
 * row above or below (a new one past the edge) — through the same drop rules
 * as the pointer, so the two cannot disagree. Null when it cannot go.
 */
export function nudge(body, element, byName, direction) {
	const rows = body?.rows || [];
	const r = rows.findIndex(row => row.some(p => p.element === element));
	if (r < 0) return null;
	const index = rows[r].findIndex(p => p.element === element);
	if (direction === 'left' || direction === 'right') {
		const to = index + (direction === 'left' ? -1 : 1);
		if (to < 0 || to >= rows[r].length) return null;
		return { body: reorderInRow(body, r, index, to), displaced: [] };
	}
	const { start, span } = extents(rows[r])[index];
	const col = start + span / 2;
	const alone = rows[r].length === 1;
	if (direction === 'up') {
		if (r === 0) return alone ? null : planDrop(body, element, byName, { type: 'newRow', at: 0 });
		return planDrop(body, element, byName, { type: 'row', row: r - 1, col });
	}
	if (r === rows.length - 1) return alone ? null : planDrop(body, element, byName, { type: 'newRow', at: rows.length });
	return planDrop(body, element, byName, { type: 'row', row: r + 1, col });
}

/**
 * Drops anything the card no longer has and re-widens what is left, so a body
 * loaded from the server (or carried across a card edit) is legal before the
 * editor shows it. The body is read through readTileBody: one that is not in
 * sixths is no layout (KPR-761) and opens empty, as a tier nobody has arranged.
 *
 * `byName` may answer a name with a field of ANOTHER name: a date range is one
 * field on a tile, found under its end's name too (itemValueDisplay
 * tileFieldsByName, KPR-626). A placement is kept under the field's own name,
 * and only the first placement of a field survives, so a stored layout that
 * placed both halves opens with the range once.
 */
export function normalize(body, byName) {
	const seen = new Set();
	const rows = (readTileBody(body)?.rows || [])
		.map(row => (row || [])
			.filter(p => {
				const field = p?.element ? byName.get(p.element) : null;
				const name = field?.name || p?.element;
				if (!field || seen.has(name)) return false;
				seen.add(name);
				return true;
			})
			.map(p => {
				const field = byName.get(p.element);
				return { element: field?.name || p.element, span: Math.max(minSpanFor(field?.dataType), p.span || 1) };
			}))
		.filter(row => row.length)
		.slice(0, TILE_MAX_ROWS);

	// Trim any row the widening pushed over six columns, from the right.
	const trimmed = rows.map(row => {
		const kept = [];
		for (const placement of row) {
			if (rowWidth(kept) + placement.span > TILE_COLS) break;
			kept.push(placement);
		}
		return kept;
	}).filter(row => row.length);

	return { cols: TILE_COLS, rows: trimmed };
}

/**
 * Is this body the same arrangement as that one? Each read through
 * readTileBody, so a body that is not in sixths is the empty arrangement.
 */
export const sameBody = (a, b) => JSON.stringify(normalizeShape(a)) === JSON.stringify(normalizeShape(b));

const normalizeShape = body => ({
	rows: (readTileBody(body)?.rows || []).map(row => row.map(p => ({ element: p.element, span: p.span || 1 }))),
});

/* ---------------------------------------------------------------------------
 * KPR-347: where a field is, why a move cannot happen, and ONE door for every
 * edit.
 *
 * Free placement on the six-column canvas, inside the stored contract: a row
 * is its fields in order with their widths (no offsets), so a row never holds
 * empty columns before or between fields. A field let go over a row's free
 * columns lands after the fields already there, and the editor's landing
 * outline shows exactly that before the release.
 *
 * Every change the editor makes to an arrangement is an EDIT passed through
 * applyTileEdit, so undo (KPR-348) has one place to record from. A refused
 * edit returns the body unchanged with a sentence saying why; nothing here
 * throws on a stale or unknown name.
 * ------------------------------------------------------------------------- */

const defaultLabel = name => name;
const NEW_ROW_REFUSAL = { code: 'rows-full', message: `No room for a new row: a tile has at most ${TILE_MAX_ROWS} rows.` };

/** Where a placed field is: its row, its index there, its first column (0-based) and its width. Null when it is not placed. */
export function positionOf(body, element) {
	const rows = body?.rows || [];
	for (let row = 0; row < rows.length; row += 1) {
		const index = rows[row].findIndex(p => p.element === element);
		if (index >= 0) {
			const { start, span } = extents(rows[row])[index];
			return { row, index, start, span };
		}
	}
	return null;
}

/** "row 2, columns 3 to 4 of 6" — what the announcer says a field's place is. */
export function describePosition(body, element) {
	const at = positionOf(body, element);
	if (!at) return 'not on the tile';
	const from = at.start + 1;
	const to = at.start + at.span;
	let columns = `columns ${from} to ${to} of ${TILE_COLS}`;
	if (at.span >= TILE_COLS) columns = `all ${TILE_COLS} columns`;
	else if (from === to) columns = `column ${from} of ${TILE_COLS}`;
	return `row ${at.row + 1}, ${columns}`;
}

/** The widths a placed field may take where it stands: its type's least, and what its row has room for. */
export function spanLimits(body, element, byName) {
	const min = minSpanFor(byName.get(element)?.dataType);
	return { min, max: Math.max(min, maxSpanFor(body, element)) };
}

/**
 * Why a placed field cannot be `requested` columns wide where it stands, or
 * null when it can. The editor holds a dragged edge to the limits and shows
 * this sentence while the pointer asks past them.
 */
export function spanRefusal(body, element, requested, byName, { labelOf = defaultLabel } = {}) {
	const label = labelOf(element);
	const at = positionOf(body, element);
	if (!at) return { code: 'not-placed', message: `${label} is not on the tile.` };
	const { min, max } = spanLimits(body, element, byName);
	if (requested < min) {
		return { code: 'below-minimum', message: `${label} needs at least ${min} of the ${TILE_COLS} columns to be read.` };
	}
	if (requested > max) {
		if (max >= TILE_COLS) return { code: 'whole-row', message: `${label} already takes the whole row.` };
		return { code: 'no-room', message: `No room to widen ${label}: the rest of row ${at.row + 1} is taken.` };
	}
	return null;
}

/**
 * A dragged edge's width: what the pointer asks for (any number of columns,
 * fractional), held to the limits, and the reason while it asks past them.
 */
export function resizeRequest(body, element, requested, byName, options) {
	const asked = Math.round(Number(requested) || 0);
	const { min, max } = spanLimits(body, element, byName);
	return {
		span: Math.max(min, Math.min(max, asked)),
		refusal: spanRefusal(body, element, asked, byName, options),
	};
}

/** The drop, falling back to the row itself when the edge's new row has no room. */
const planOrFallback = (body, element, byName, target) => (
	planDrop(body, element, byName, target)
	|| (target?.fallback ? planDrop(body, element, byName, target.fallback) : null)
);

/** What a drag would do if let go at `target` now: the plan, or null. */
export const planTileDrop = planOrFallback;

/**
 * Why `element` cannot be let go at `target`, or null when it can. A target of
 * null is the pointer off the tile body; `fromBody` says the field is already
 * on the tile (so the list is where it would come off).
 */
export function dropRefusal(body, element, byName, target, { labelOf = defaultLabel, fromBody = false } = {}) {
	const label = labelOf(element);
	const dataType = byName.get(element)?.dataType;
	if (!dataType) return { code: 'unknown', message: `${label} is not a field of this card any more.` };
	if (!target) {
		return {
			code: 'outside',
			message: fromBody
				? `Let go over the tile body to move ${label}, or over Fields to take it off.`
				: `Let go over the tile body to place ${label}.`,
		};
	}
	if (target.type === 'remove') return null;
	if (planOrFallback(body, element, byName, target)) return null;
	// A row's edge with no room for a new row aims at the row itself, which
	// is what turns red: name that.
	if (target.type === 'newRow') {
		return target.fallback ? dropRefusal(body, element, byName, target.fallback, { labelOf, fromBody }) : NEW_ROW_REFUSAL;
	}
	return {
		code: 'no-room',
		message: `No room on row ${target.row + 1} for ${label}: it needs ${minSpanFor(dataType)} columns, and the fields there have nowhere to move.`,
	};
}

/**
 * Why the keyboard cannot move a placed field that way (nudge answered null).
 */
function nudgeRefusal(body, element, byName, direction, { labelOf }) {
	const label = labelOf(element);
	const at = positionOf(body, element);
	const rows = body?.rows || [];
	if (direction === 'left') return { code: 'edge', message: `${label} is already first on row ${at.row + 1}.` };
	if (direction === 'right') return { code: 'edge', message: `${label} is already last on row ${at.row + 1}.` };
	if (direction !== 'up' && direction !== 'down') return { code: 'unknown', message: `${label} cannot move that way.` };
	const toRow = at.row + (direction === 'up' ? -1 : 1);
	if (toRow < 0 || toRow >= rows.length) {
		if (rows[at.row].length === 1) {
			return { code: 'edge', message: `${label} is already alone on the ${direction === 'up' ? 'top' : 'bottom'} row.` };
		}
		return NEW_ROW_REFUSAL;
	}
	return dropRefusal(body, element, byName, { type: 'row', row: toRow, col: at.start + at.span / 2 }, { labelOf });
}

/**
 * THE ONE DOOR. Applies an edit to a body and says what happened:
 *   { body, displaced: [names], refusal: null | { code, message } }
 * A refused edit returns the body it was given, unchanged.
 *
 * Edits:
 *   { type: 'insert', element, row? }        from the list; no `row`: the first row with room, else a new row
 *   { type: 'remove', element }              off the tile
 *   { type: 'span', element, span }          a width the field's row has room for
 *   { type: 'drop', element, target, fromBody? }  a pointer's release at a planDrop target
 *                                            ({ type: 'remove' } = over the list; null = off the tile)
 *   { type: 'nudge', element, direction }    the keyboard: left, right, up, down
 *   { type: 'restore', body }                put back an arrangement held earlier (Escape after a pick-up)
 */
export function applyTileEdit(body, edit, byName, { labelOf = defaultLabel } = {}) {
	const same = refusal => ({ body, displaced: [], refusal });
	const changed = (next, displaced = []) => ({ body: next, displaced, refusal: null });
	if (!edit || typeof edit !== 'object') return same({ code: 'unknown', message: 'Nothing to change.' });

	if (edit.type === 'restore') return changed(clone(edit.body));

	const { element } = edit;
	const label = labelOf(element);
	if (!byName.get(element)?.dataType) {
		return same({ code: 'unknown', message: `${label} is not a field of this card any more.` });
	}
	const notPlaced = () => same({ code: 'not-placed', message: `${label} is not on the tile.` });

	switch (edit.type) {
		case 'insert': {
			const where = canPlace(body, element, byName);
			if (where.reason === 'already-placed') return same({ code: 'already-placed', message: `${label} is already on the tile.` });
			if (where.reason === 'full') return same({ code: 'full', message: 'The tile is full — remove or narrow a field first.' });
			const row = edit.row === undefined || edit.row === null
				? (where.rows.length ? where.rows[0] : null)
				: edit.row;
			const next = addPlacement(body, element, byName, row);
			if (next === body) {
				return same(row === null ? NEW_ROW_REFUSAL : { code: 'no-room', message: `No room on row ${row + 1} for ${label}.` });
			}
			return changed(next);
		}
		case 'remove':
			if (!positionOf(body, element)) return notPlaced();
			return changed(removePlacement(body, element));
		case 'span': {
			if (!Number.isInteger(edit.span)) return same({ code: 'not-whole', message: 'A width is a whole number of columns.' });
			const refusal = spanRefusal(body, element, edit.span, byName, { labelOf });
			if (refusal) return same(refusal);
			return changed(setSpan(body, element, edit.span, byName));
		}
		case 'drop': {
			if (edit.target?.type === 'remove') {
				if (!positionOf(body, element)) return notPlaced();
				return changed(removePlacement(body, element));
			}
			const plan = edit.target ? planOrFallback(body, element, byName, edit.target) : null;
			if (!plan) return same(dropRefusal(body, element, byName, edit.target, { labelOf, fromBody: edit.fromBody }));
			return changed(plan.body, plan.displaced);
		}
		case 'nudge': {
			if (!positionOf(body, element)) return notPlaced();
			const moved = nudge(body, element, byName, edit.direction);
			if (moved) return changed(moved.body, moved.displaced);
			return same(nudgeRefusal(body, element, byName, edit.direction, { labelOf }));
		}
		default:
			return same({ code: 'unknown', message: 'Nothing to change.' });
	}
}

/**
 * What keepr-api's validateTileBody would refuse in this body, in sixths, or
 * null. The editor's edits cannot produce one; Save is held on it anyway, so
 * a body that would be refused is never sent.
 */
export function tileBodyProblem(body, byName) {
	if (!body || typeof body !== 'object' || Array.isArray(body)) return 'The tile body is not an arrangement.';
	const keys = Object.keys(body).filter(key => !['rows', 'tags', 'cols'].includes(key));
	if (keys.length) return `The tile body has keys a tile does not take: ${keys.join(', ')}.`;
	if (body.cols !== TILE_COLS) return `The tile body is not in ${TILE_COLS} columns.`;
	if (body.tags !== undefined && typeof body.tags !== 'boolean') return 'Show tags is on or off.';
	const rows = body.rows === undefined ? [] : body.rows;
	if (!Array.isArray(rows)) return 'The tile body has no rows.';
	if (rows.length > TILE_MAX_ROWS) return `A tile holds at most ${TILE_MAX_ROWS} rows.`;
	const seen = new Set();
	for (let r = 0; r < rows.length; r += 1) {
		const row = rows[r];
		if (!Array.isArray(row) || !row.length) return `Row ${r + 1} is empty.`;
		if (row.length > TILE_COLS) return `Row ${r + 1} holds more than ${TILE_COLS} fields.`;
		let width = 0;
		for (const p of row) {
			if (!p || typeof p !== 'object' || Array.isArray(p)) return `Row ${r + 1} holds something that is not a field.`;
			if (Object.keys(p).some(key => key !== 'element' && key !== 'span')) return `A field on row ${r + 1} carries more than its name and width.`;
			const field = byName.get(p.element);
			if (!field || (field.name && field.name !== p.element)) return `${p.element} is not a field of this card.`;
			if (seen.has(p.element)) return `${p.element} is on the tile twice.`;
			seen.add(p.element);
			if (!Number.isInteger(p.span) || p.span < 1 || p.span > TILE_COLS) return `${p.element} has a width that is not 1 to ${TILE_COLS} columns.`;
			if (p.span < minSpanFor(field.dataType)) return `${p.element} is narrower than its type can be read in.`;
			width += p.span;
		}
		if (width > TILE_COLS) return `Row ${r + 1} is ${width} columns wide; a tile is ${TILE_COLS}.`;
	}
	return null;
}
